

const WEBENGAGE_LICENSE_CODE = process.env.REACT_APP_WEBENGAGE_LICENSE_CODE;
const WEBENGAGE_SCRIPT_ID = "_webengage_script_loader";
const LOG_PREFIX = "[WebEngage:init]";

// Always load SDK over HTTPS — localhost is http:, and the http:// widget URL
// often fails to load, leaving commands stuck in webengage.__queue.
const WEBENGAGE_SCRIPT_SRC =
  "https://widgets.in.webengage.com/js/webengage-min-v-6.0.js";

const getQueueLength = () =>
  Array.isArray(window.webengage?.__queue)
    ? window.webengage.__queue.length
    : -1;

const hasWebEngageLicenseCode = () =>
  Boolean(WEBENGAGE_LICENSE_CODE && String(WEBENGAGE_LICENSE_CODE).trim());

const installWebEngageStub = () => {
  function o(e, t) {
    e[t[t.length - 1]] = function () {
      r.__queue.push([t.join("."), arguments]);
    };
  }

  var i,
    s,
    r = window.webengage,
    z = " ",
    l = "init options track screen onReady".split(z),
    a = "webPersonalization feedback survey notification notificationInbox".split(
      z
    ),
    c = "options render clear abort".split(z),
    p = "Prepare Render Open Close Submit Complete View Click".split(z),
    u = "identify login logout setAttribute".split(z);

  // Official stub uses __queue + __v. Real SDK later replaces this object.
  if (!r || !r.__v) {
    for (
      window.webengage = r = { __queue: [], __v: "6.0", user: {} }, i = 0;
      i < l.length;
      i++
    ) {
      o(r, [l[i]]);
    }
    for (i = 0; i < a.length; i++) {
      for (r[a[i]] = {}, s = 0; s < c.length; s++) o(r[a[i]], [a[i], c[s]]);
      for (s = 0; s < p.length; s++) o(r[a[i]], [a[i], "on" + p[s]]);
    }
    for (i = 0; i < u.length; i++) o(r.user, ["user", u[i]]);
  }

  console.log(`${LOG_PREFIX} stub installed`, {
    host: window.location.hostname,
    hasTrack: typeof window.webengage?.track === "function",
    hasInit: typeof window.webengage?.init === "function",
    queueLength: getQueueLength(),
  });

  return r;
};

const callWebEngageInit = () => {
  if (!hasWebEngageLicenseCode()) {
    console.error("[WebEngage] Missing REACT_APP_WEBENGAGE_LICENSE_CODE");
    return;
  }

  if (!window.webengage?.init) {
    console.error(`${LOG_PREFIX} webengage.init unavailable`);
    return;
  }

  console.log(`${LOG_PREFIX} calling webengage.init`, {
    // License is env-based (staging in development, production in production builds).
    hasLicense: true,
  });
  window.webengage.init(WEBENGAGE_LICENSE_CODE);

  if (typeof window.webengage.onReady === "function") {
    window.webengage.onReady(() => {
      window.__webengageReady = true;
      console.log(`${LOG_PREFIX} onReady fired — SDK is live`, {
        host: window.location.hostname,
        queueLength: getQueueLength(),
        hasTrack: typeof window.webengage?.track === "function",
      });
    });
  }

  // If onReady never fires, SDK likely blocked by domain license or failed boot.
  setTimeout(() => {
    if (window.__webengageReady) return;

    console.warn(`${LOG_PREFIX} onReady did not fire within 8s`, {
      host: window.location.hostname,
      queueLength: getQueueLength(),
      scriptInDom: Boolean(document.getElementById(WEBENGAGE_SCRIPT_ID)),
      hint:
        "If queue is still growing and no api/widgets network calls appear, either the SDK script failed after load or the license is domain-locked. Add localhost in WebEngage Dashboard → Integrations → Website → Domain.",
    });
  }, 8000);
};

/**
 * Injects the real WebEngage script and initializes after it loads.
 */
export const loadRemoteWebEngageSdk = () => {
  if (document.getElementById(WEBENGAGE_SCRIPT_ID)) {
    console.log(`${LOG_PREFIX} script already in DOM — skipping inject`);
    return;
  }

  console.log(`${LOG_PREFIX} loadRemoteWebEngageSdk() called`, {
    src: WEBENGAGE_SCRIPT_SRC,
  });

  const script = document.createElement("script");
  script.id = WEBENGAGE_SCRIPT_ID;
  script.type = "text/javascript";
  script.async = true;
  script.src = WEBENGAGE_SCRIPT_SRC;

  script.onload = () => {
    console.log(`${LOG_PREFIX} script loaded successfully`, {
      src: script.src,
      queueLengthBeforeInit: getQueueLength(),
    });
    // Prefer init after script load so the real SDK (not only stub) handles it.
    callWebEngageInit();
  };

  script.onerror = (error) => {
    console.error(`${LOG_PREFIX} script failed to load`, {
      src: script.src,
      error,
      queueLength: getQueueLength(),
      hint: "No network requests will occur until this script loads.",
    });
  };

  const target = document.head || document.body || document.documentElement;
  target.appendChild(script);

  console.log(`${LOG_PREFIX} script tag injected into DOM`, {
    id: WEBENGAGE_SCRIPT_ID,
    parent: target.nodeName,
  });
};

export const initWebEngage = () => {
  console.log(`${LOG_PREFIX} initWebEngage() invoked`, {
    host: window.location?.hostname,
    href: window.location?.href,
    alreadyInitialized: Boolean(window.__webengageInitialized),
  });

  if (typeof window === "undefined" || window.__webengageInitialized) {
    console.log(`${LOG_PREFIX} skipped — already initialized or no window`);
    return;
  }

  // License code must come from env (never hardcoded in source).
  // Development → Staging account; Production → Production account.
  if (!hasWebEngageLicenseCode()) {
    console.error("[WebEngage] Missing REACT_APP_WEBENGAGE_LICENSE_CODE");
    return;
  }

  window.__webengageInitialized = true;
  window.__webengageReady = false;

  try {
    installWebEngageStub();
    // Queue init early as well (official pattern); onload will call init again safely.
    if (window.webengage?.init) {
      window.webengage.init(WEBENGAGE_LICENSE_CODE);
      console.log(`${LOG_PREFIX} init queued on stub (pre-script)`, {
        queueLength: getQueueLength(),
      });
    }
    loadRemoteWebEngageSdk();
  } catch (error) {
    console.error(`${LOG_PREFIX} SDK initialization failed`, error);
  }
};
